/* READY-MADE MOVES — the catalogue, and the arithmetic that turns one into
   keyframes. Pure in the same sense core/anim.js is: no DOM, no three, no S,
   no clock. Give it a move, the pose an object is in, and a little context,
   and it hands back the keys to write. state/moves.js does the writing.

   The point of the split is that a move is DATA. Adding "Slide in from above"
   is a row in MOVES, not a function somewhere — and every row can be checked
   by the test suite without a browser.

   What a move is NOT: a second animation system. Applying one writes ordinary
   keyframes onto ordinary tracks, and from that moment the app has no idea a
   move was ever involved. They can be dragged, re-eased, deleted one at a
   time, and exported, exactly like keys placed by hand. That is also why
   nothing here is stored in the document. */

import { copyValue } from './anim.js';

/* The families, in the order the picker lists them. Text last because it is
   the only one gated on what the object IS rather than on what the move does —
   every other group is offered on nearly everything, and this one appears on
   a Text Area alone. */
export const MOVE_GROUPS = ['Entrance', 'Exit', 'Loop', 'Depth', 'Text'];

/* What each family is called in the picker. Beside the list rather than in
   the panel, where it had grown into a chain of ternaries pluralising some
   names and not others — adding a group meant remembering to add an exception
   too, and "Texts" is what you get when you forget. */
export const GROUP_LABELS = {
  Entrance: 'Entrances', Exit: 'Exits', Loop: 'Loops', Depth: 'Depth', Text: 'Text'
};

/* ── how a move says what it wants ─────────────────────────────────────────

   { id, name, group, hint,
     length,            seconds — the default the Length field opens on
     span,              'length' (a one-shot) or 'duration' (fills the timeline)
     needs: ['scale'],  the props it writes. ALSO the legality test: a move is
                        offered only when every one is in animatableProps(o),
                        which is why a spin needs no billboard special case —
                        that function already drops rot for a facing object.
     refuse: o => …,    rare; a rule about the object rather than its props
     tracks: { prop: [ { at, v, ease } ] } }

   `at` is a FRACTION of the span, not seconds, so one Length field retimes
   every key including the overshoots. `v(base, ctx)` returns the WHOLE
   property value the model stores — a vector for pos/rot/scale, a number for
   opacity — and `ease` is always stated, because setKey would otherwise
   inherit whatever ease happened to be on a key it replaces.

   `base` is the pose the object is in when the move is applied, captured
   before anything is written. `ctx` is { length, span, duration, fps, unit }.

   DISTANCES ARE FRACTIONS OF `unit`, never metres. unit is the short side of
   the screen, the same basis core/doc.js sizes everything else against. A
   literal "1.5 m to the left" is a gentle nudge on a shop window and three
   screen-widths off-stage on a 55-inch panel; 0.45 × unit reads the same on
   both. */

const add = (b, d) => ({ x: b.x + (d.x || 0), y: b.y + (d.y || 0), z: b.z + (d.z || 0) });
const mul = (b, k) => ({ x: b.x * k, y: b.y * k, z: b.z * k });

/* Not exactly zero. A zero scale is a singular matrix — three tolerates it,
   but normals and the shadow map are undefined for that frame, and "from
   nothing" is indistinguishable at a thousandth. */
const NOTHING = { x: 0.001, y: 0.001, z: 0.001 };

/* An object sitting at opacity 0 has nothing to fade up TO, and a move that
   quietly animates from 0 to 0 reads as broken. Full opacity is the only
   sensible reading of "fade this in". */
const solid = b => (b.opacity == null || b.opacity <= 0.02 ? 1 : b.opacity);

/* Never all the way to zero on a loop: applyVisibility removes the node
   entirely at 0 (scene/objects.js), so a pulse through it would stop casting
   a shadow for a frame and flicker against whatever stands behind it. */
const dim = b => Math.max(0.15, solid(b) * 0.35);

/* A lamp's brightness, in candela or lux depending on its kind. Same guard as
   solid(): a lamp turned down to nothing has nothing to swell from. */
const lamp = b => (Number.isFinite(b.intensity) && b.intensity > 0 ? b.intensity : 1);

export const MOVES = [
  /* ── Entrances ───────────────────────────────────────────────────────────
     Two-key moves are the ones to reach for first: they read at 1 s and still
     read at 0.4 s. The three-key ones want 0.6 s or more, so the settle is
     more than a handful of frames. */
  { id: 'scale-in', name: 'Scale in', group: 'Entrance',
    hint: 'Grows from nothing to its full size.',
    length: 1, span: 'length', needs: ['scale'],
    tracks: { scale: [{ at: 0, v: () => copyValue(NOTHING), ease: 'out' },
                      { at: 1, v: b => copyValue(b.scale), ease: 'inout' }] } },

  { id: 'pop-in', name: 'Pop in', group: 'Entrance',
    hint: 'Grows past its size and settles back.',
    length: 0.6, span: 'length', needs: ['scale'],
    tracks: { scale: [{ at: 0, v: () => copyValue(NOTHING), ease: 'back' },
                      { at: 1, v: b => copyValue(b.scale), ease: 'inout' }] } },

  { id: 'spring-in', name: 'Spring in', group: 'Entrance',
    hint: 'Overshoots and wobbles to a stop.',
    length: 1.1, span: 'length', needs: ['scale'],
    tracks: { scale: [{ at: 0, v: () => copyValue(NOTHING), ease: 'elastic' },
                      { at: 1, v: b => copyValue(b.scale), ease: 'inout' }] } },

  { id: 'fade-in', name: 'Fade in', group: 'Entrance',
    hint: 'Nothing to fully visible.',
    length: 1, span: 'length', needs: ['opacity'],
    tracks: { opacity: [{ at: 0, v: () => 0, ease: 'out' },
                        { at: 1, v: solid, ease: 'inout' }] } },

  { id: 'slide-in-left', name: 'Slide in from the left', group: 'Entrance',
    hint: 'Enters from off the left edge.',
    length: 1, span: 'length', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: (b, c) => add(b.pos, { x: -0.45 * c.unit }), ease: 'out' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  { id: 'slide-in-right', name: 'Slide in from the right', group: 'Entrance',
    hint: 'Enters from off the right edge.',
    length: 1, span: 'length', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: (b, c) => add(b.pos, { x: 0.45 * c.unit }), ease: 'out' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  { id: 'rise-in', name: 'Rise in', group: 'Entrance',
    hint: 'Lifts into place as it fades up.',
    length: 1, span: 'length', needs: ['pos', 'opacity'],
    tracks: {
      pos: [{ at: 0, v: (b, c) => add(b.pos, { y: -0.18 * c.unit }), ease: 'back' },
            { at: 1, v: b => copyValue(b.pos), ease: 'inout' }],
      opacity: [{ at: 0, v: () => 0, ease: 'out' },
                { at: 0.55, v: solid, ease: 'inout' }]
    } },

  { id: 'drop-bounce', name: 'Drop in and bounce', group: 'Entrance',
    hint: 'Falls in and bounces to rest.',
    length: 1.2, span: 'length', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: (b, c) => add(b.pos, { y: 0.4 * c.unit }), ease: 'bounce' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  { id: 'spin-in', name: 'Spin in', group: 'Entrance',
    hint: 'Turns half a circle as it grows.',
    length: 1, span: 'length', needs: ['rot', 'scale'],
    tracks: {
      rot: [{ at: 0, v: b => add(b.rot, { y: -180 }), ease: 'out' },
            { at: 1, v: b => copyValue(b.rot), ease: 'inout' }],
      scale: [{ at: 0, v: () => copyValue(NOTHING), ease: 'out' },
              { at: 1, v: b => copyValue(b.scale), ease: 'inout' }]
    } },

  { id: 'slide-in-top', name: 'Slide in from above', group: 'Entrance',
    hint: 'Drops in from above the screen and settles.',
    length: 1, span: 'length', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: (b, c) => add(b.pos, { y: 0.45 * c.unit }), ease: 'out' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  { id: 'slide-in-bottom', name: 'Slide in from below', group: 'Entrance',
    hint: 'Rises in from under the screen.',
    length: 1, span: 'length', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: (b, c) => add(b.pos, { y: -0.45 * c.unit }), ease: 'out' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  /* The quietest entrance in the set: a small growth rather than a growth from
     nothing, which is what makes it read as arriving rather than inflating. */
  { id: 'grow-fade-in', name: 'Fade and grow', group: 'Entrance',
    hint: 'Fades up while growing the last little way.',
    length: 1, span: 'length', needs: ['scale', 'opacity'],
    tracks: {
      scale: [{ at: 0, v: b => mul(b.scale, 0.86), ease: 'out' },
              { at: 1, v: b => copyValue(b.scale), ease: 'inout' }],
      opacity: [{ at: 0, v: () => 0, ease: 'out' },
                { at: 1, v: solid, ease: 'inout' }]
    } },

  { id: 'flip-in', name: 'Flip in', group: 'Entrance',
    hint: 'Tips upright from lying flat, with a small overshoot.',
    length: 0.9, span: 'length', needs: ['rot'],
    tracks: { rot: [{ at: 0, v: b => add(b.rot, { x: -90 }), ease: 'back' },
                    { at: 1, v: b => copyValue(b.rot), ease: 'inout' }] } },

  { id: 'swing-in', name: 'Swing in', group: 'Entrance',
    hint: 'Swings upright and rocks to a stop, as if hung on a nail.',
    length: 1.1, span: 'length', needs: ['rot'],
    tracks: { rot: [{ at: 0, v: b => add(b.rot, { z: -14 }), ease: 'elastic' },
                    { at: 1, v: b => copyValue(b.rot), ease: 'inout' }] } },

  /* ── Exits ───────────────────────────────────────────────────────────────
     Shorter than the entrances by default. Leaving is not the part anyone
     wants to watch. */
  { id: 'fade-out', name: 'Fade out', group: 'Exit',
    hint: 'Fully visible to nothing.',
    length: 1, span: 'length', needs: ['opacity'],
    tracks: { opacity: [{ at: 0, v: solid, ease: 'in' },
                        { at: 1, v: () => 0, ease: 'inout' }] } },

  { id: 'scale-out', name: 'Scale out', group: 'Exit',
    hint: 'Shrinks away to nothing.',
    length: 0.8, span: 'length', needs: ['scale'],
    tracks: { scale: [{ at: 0, v: b => copyValue(b.scale), ease: 'in' },
                      { at: 1, v: () => copyValue(NOTHING), ease: 'inout' }] } },

  { id: 'slide-out-right', name: 'Slide out to the right', group: 'Exit',
    hint: 'Leaves past the right edge, fading as it goes.',
    length: 0.8, span: 'length', needs: ['pos', 'opacity'],
    tracks: {
      pos: [{ at: 0, v: b => copyValue(b.pos), ease: 'in' },
            { at: 1, v: (b, c) => add(b.pos, { x: 0.45 * c.unit }), ease: 'inout' }],
      /* Holds its opacity for the first half: fading from the first frame
         makes the slide look like a mistake rather than a departure. */
      opacity: [{ at: 0, v: solid, ease: 'in' },
                { at: 0.45, v: solid, ease: 'in' },
                { at: 1, v: () => 0, ease: 'inout' }]
    } },

  { id: 'drop-out', name: 'Drop out', group: 'Exit',
    hint: 'Falls away and fades.',
    length: 0.8, span: 'length', needs: ['pos', 'opacity'],
    tracks: {
      pos: [{ at: 0, v: b => copyValue(b.pos), ease: 'in' },
            { at: 1, v: (b, c) => add(b.pos, { y: -0.35 * c.unit }), ease: 'inout' }],
      opacity: [{ at: 0, v: solid, ease: 'in' },
                { at: 0.5, v: solid, ease: 'in' },
                { at: 1, v: () => 0, ease: 'inout' }]
    } },

  { id: 'slide-out-left', name: 'Slide out to the left', group: 'Exit',
    hint: 'Leaves past the left edge, fading as it goes.',
    length: 0.8, span: 'length', needs: ['pos', 'opacity'],
    tracks: {
      pos: [{ at: 0, v: b => copyValue(b.pos), ease: 'in' },
            { at: 1, v: (b, c) => add(b.pos, { x: -0.45 * c.unit }), ease: 'inout' }],
      opacity: [{ at: 0, v: solid, ease: 'in' },
                { at: 0.45, v: solid, ease: 'in' },
                { at: 1, v: () => 0, ease: 'inout' }]
    } },

  /* Shrinks a little rather than to nothing: at this length the eye reads the
     fade, and a collapse to zero on top of it looks like two exits at once. */
  { id: 'shrink-fade-out', name: 'Shrink and fade', group: 'Exit',
    hint: 'Softens away instead of snapping off.',
    length: 0.9, span: 'length', needs: ['scale', 'opacity'],
    tracks: {
      scale: [{ at: 0, v: b => copyValue(b.scale), ease: 'in' },
              { at: 1, v: b => mul(b.scale, 0.82), ease: 'inout' }],
      opacity: [{ at: 0, v: solid, ease: 'in' },
                { at: 1, v: () => 0, ease: 'inout' }]
    } },

  { id: 'flip-out', name: 'Flip out', group: 'Exit',
    hint: 'Tips away from the viewer and fades.',
    length: 0.9, span: 'length', needs: ['rot', 'opacity'],
    tracks: {
      rot: [{ at: 0, v: b => copyValue(b.rot), ease: 'in' },
            { at: 1, v: b => add(b.rot, { x: 90 }), ease: 'inout' }],
      opacity: [{ at: 0, v: solid, ease: 'in' },
                { at: 0.55, v: solid, ease: 'in' },
                { at: 1, v: () => 0, ease: 'inout' }]
    } },

  /* ── Loops ───────────────────────────────────────────────────────────────
     span:'duration', so the first key is at 0 and the last at the end of the
     timeline, holding the same value. Every one of these puts a TURNING POINT
     at the two ends rather than the middle of a swing: 'inout' brings the
     value to a standstill at a keyframe, and a standstill is only correct
     where the motion actually reverses. Put the seam mid-swing and a screen
     playing all day hitches once a cycle. */
  { id: 'breathe', name: 'Breathe', group: 'Loop',
    hint: 'Swells a little and settles, forever.',
    span: 'duration', needs: ['scale'],
    tracks: { scale: [{ at: 0, v: b => copyValue(b.scale), ease: 'inout' },
                      { at: 0.5, v: b => mul(b.scale, 1.06), ease: 'inout' },
                      { at: 1, v: b => copyValue(b.scale), ease: 'inout' }] } },

  { id: 'bob', name: 'Bob', group: 'Loop',
    hint: 'Drifts up and back down.',
    span: 'duration', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: b => copyValue(b.pos), ease: 'inout' },
                    { at: 0.5, v: (b, c) => add(b.pos, { y: 0.04 * c.unit }), ease: 'inout' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  { id: 'sway', name: 'Sway', group: 'Loop',
    hint: 'Rocks gently side to side.',
    span: 'duration', needs: ['rot'],
    tracks: { rot: [{ at: 0, v: b => add(b.rot, { z: 3 }), ease: 'inout' },
                    { at: 0.5, v: b => add(b.rot, { z: -3 }), ease: 'inout' },
                    { at: 1, v: b => add(b.rot, { z: 3 }), ease: 'inout' }] } },

  { id: 'turn', name: 'Turn on the spot', group: 'Loop',
    hint: 'One full revolution, at a steady speed.',
    span: 'duration', needs: ['rot'],
    /* 'auto', not 'linear' and certainly not 'inout'. A revolution must not
       stop at a keyframe, and 'inout' brings it to a halt at every one. Auto
       is the ease built for a key that is a waypoint rather than a
       destination: it passes through at the speed its neighbours imply, which
       across evenly spaced keys is a constant rate — the same motion linear
       gives, and it stays smooth if the keys are ever moved. */
    tracks: { rot: [{ at: 0, v: b => copyValue(b.rot), ease: 'auto' },
                    { at: 0.5, v: b => add(b.rot, { y: 180 }), ease: 'auto' },
                    { at: 1, v: b => add(b.rot, { y: 360 }), ease: 'auto' }] } },

  { id: 'pulse', name: 'Pulse', group: 'Loop',
    hint: 'Dims and comes back.',
    span: 'duration', needs: ['opacity'],
    tracks: { opacity: [{ at: 0, v: solid, ease: 'inout' },
                        { at: 0.5, v: dim, ease: 'inout' },
                        { at: 1, v: solid, ease: 'inout' }] } },

  { id: 'drift', name: 'Drift', group: 'Loop',
    hint: 'Slides a little way across and back.',
    span: 'duration', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: (b, c) => add(b.pos, { x: -0.03 * c.unit }), ease: 'inout' },
                    { at: 0.5, v: (b, c) => add(b.pos, { x: 0.03 * c.unit }), ease: 'inout' },
                    { at: 1, v: (b, c) => add(b.pos, { x: -0.03 * c.unit }), ease: 'inout' }] } },

  { id: 'nod', name: 'Nod', group: 'Loop',
    hint: 'Tips gently forward and back.',
    span: 'duration', needs: ['rot'],
    tracks: { rot: [{ at: 0, v: b => add(b.rot, { x: -3 }), ease: 'inout' },
                    { at: 0.5, v: b => add(b.rot, { x: 3 }), ease: 'inout' },
                    { at: 1, v: b => add(b.rot, { x: -3 }), ease: 'inout' }] } },

  /* The only move written for a lamp. `intensity` is a light's own version of
     opacity, so this is offered exactly where the others are not — no rule
     here says "lights only", it falls out of the property it names. */
  { id: 'throb', name: 'Throb', group: 'Loop',
    hint: 'A lamp swelling and settling, forever. Lights only.',
    span: 'duration', needs: ['intensity'],
    tracks: { intensity: [{ at: 0, v: lamp, ease: 'inout' },
                          { at: 0.5, v: b => lamp(b) * 0.55, ease: 'inout' },
                          { at: 1, v: lamp, ease: 'inout' }] } },

  /* ── Depth ───────────────────────────────────────────────────────────────
     The axis the whole illusion is built on: +Z is towards the viewer, so
     these are the moves that read as coming out of the wall. No general
     animation library has them, because no general animation library knows
     where the audience is standing. */
  { id: 'step-out', name: 'Step out of the screen', group: 'Depth',
    hint: 'Comes forward from inside the wall, past its place, and settles.',
    length: 1.2, span: 'length', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: (b, c) => add(b.pos, { z: -0.25 * c.unit }), ease: 'back' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  { id: 'sink-in', name: 'Sink into the wall', group: 'Depth',
    hint: 'Retreats behind the screen.',
    length: 1.2, span: 'length', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: b => copyValue(b.pos), ease: 'in' },
                    { at: 1, v: (b, c) => add(b.pos, { z: -0.3 * c.unit }), ease: 'inout' }] } },

  { id: 'push-through', name: 'Push through and back', group: 'Depth',
    hint: 'Leans out towards the viewer and returns, forever.',
    span: 'duration', needs: ['pos'],
    tracks: { pos: [{ at: 0, v: b => copyValue(b.pos), ease: 'inout' },
                    { at: 0.5, v: (b, c) => add(b.pos, { z: 0.22 * c.unit }), ease: 'inout' },
                    { at: 1, v: b => copyValue(b.pos), ease: 'inout' }] } },

  { id: 'lean-out', name: 'Lean out', group: 'Depth',
    hint: 'Tips forward out of the frame. Hinges wherever its pivot is set.',
    length: 1, span: 'length', needs: ['rot'],
    tracks: { rot: [{ at: 0, v: b => copyValue(b.rot), ease: 'back' },
                    { at: 1, v: b => add(b.rot, { x: 12 }), ease: 'inout' }] } },

  /* Further back than step-out, and fading, so the wall reads as opaque until
     the object is already through it. The longest default in the set: depth
     needs time or it reads as a scale change instead. */
  { id: 'emerge', name: 'Emerge from the wall', group: 'Depth',
    hint: 'Comes forward out of the dark as it appears.',
    length: 1.4, span: 'length', needs: ['pos', 'opacity'],
    tracks: {
      pos: [{ at: 0, v: (b, c) => add(b.pos, { z: -0.45 * c.unit }), ease: 'out' },
            { at: 1, v: b => copyValue(b.pos), ease: 'inout' }],
      opacity: [{ at: 0, v: () => 0, ease: 'out' },
                { at: 0.7, v: solid, ease: 'inout' }]
    } },

  { id: 'rock-out', name: 'Rock out and back', group: 'Depth',
    hint: 'Tips towards the viewer and back again, forever.',
    span: 'duration', needs: ['rot'],
    tracks: { rot: [{ at: 0, v: b => copyValue(b.rot), ease: 'inout' },
                    { at: 0.5, v: b => add(b.rot, { x: 7 }), ease: 'inout' },
                    { at: 1, v: b => copyValue(b.rot), ease: 'inout' }] } },

  /* ── Text ────────────────────────────────────────────────────────────────
     Its own family rather than scattered through Entrances and Exits, because
     these are not "another way in" — they are the only moves that animate the
     WORDS instead of the block they sit on, and the only ones a Text Area has
     that nothing else does. Grouped, they are findable; filed under Entrances
     they were two rows among a dozen that all read alike.

     TEXT AREA ONLY, and that needs no special case to say so: `reveal` is in
     animatableProps for nothing else, and `needs` is already the legality
     test. A 3D Text block is one extruded mesh, with no separate words in
     it — so on anything but a Text Area this whole group is simply absent. */
  { id: 'words-in', name: 'Words in', group: 'Text',
    hint: 'Brings the text in a word at a time.',
    length: 1.6, span: 'length', needs: ['reveal'],
    /* THE ONE LINEAR MOVE IN THE LIBRARY, on purpose. Everywhere else an
       eased curve reads better, but this value is a POSITION IN A LIST rather
       than a distance: easing it bunches the words at one end and thins them
       at the other, which reads as a stutter, not as acceleration. The smooth
       part is each word's own fade, and scene/textarea.js owns that. */
    tracks: { reveal: [{ at: 0, v: () => 0, ease: 'linear' },
                       { at: 1, v: () => 1, ease: 'linear' }] } },

  /* The reveal read backwards, so the LAST word goes first and the sentence
     un-writes itself. Quicker than it arrives: reading is the slow part, and
     by the time this runs it has been read. */
  { id: 'words-out', name: 'Words out', group: 'Text',
    hint: 'Takes the text away a word at a time, first word first.',
    length: 1.2, span: 'length', needs: ['erase'],
    /* The TRAILING edge, not the leading one run backwards. Reading is
       left to right, so the sentence should empty the way it filled —
       from the front — and the eye follows the gap rather than waiting at
       the end for a word that has already been read to disappear.

       Animating Reveal 1 → 0 by hand still gives the other exit, where the
       last word goes first; nothing was taken away by changing this. */
    tracks: { erase: [{ at: 0, v: () => 0, ease: 'linear' },
                      { at: 1, v: () => 1, ease: 'linear' }] } }
];

export const moveById = id => MOVES.find(m => m.id === id) || null;

/**
 * The moves this object can honour.
 *
 * `legalProps` is animatableProps(o) — the per-object answer, not ANIMATABLE.
 * A move is offered only when every property it writes is in there, which is
 * how a billboarded object loses the spins and an inset loses the slides,
 * with no rule repeated here to fall out of step with that one.
 */
export function movesFor(legalProps, o) {
  const legal = new Set(legalProps || []);
  return MOVES.filter(m => m.needs.every(p => legal.has(p)) && !(m.refuse && m.refuse(o)));
}

/** The most keys any one track of this move carries. */
const widest = move => Math.max(...Object.values(move.tracks).map(ks => ks.length));

/**
 * The shortest length this move still reads at: two frames per segment.
 *
 * Below about four frames a settle stops being a settle and becomes a glitch,
 * and below two the keys quantise onto the same frame and setKey merges them,
 * which silently drops part of the shape.
 */
export const minLength = (move, frameDur) => +((widest(move) - 1) * 2 * frameDur).toFixed(4);

/**
 * The keys a move would write, as { prop: [{ t, v, ease }] }.
 *
 * `t` is SECONDS FROM THE START OF THE MOVE and is not quantised — the caller
 * offsets it by where the move starts and snaps it to a frame, because only
 * the caller knows the playhead and the frame rate.
 *
 * Pure: it reads `base` and `ctx` and touches nothing.
 */
export function buildMoveKeys(move, base, ctx) {
  const out = {};
  for (const [prop, keys] of Object.entries(move.tracks))
    out[prop] = keys.map(k => ({ t: k.at * ctx.span, v: k.v(base, ctx), ease: k.ease }));
  return out;
}
